import { t } from "@lingui/macro";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PreloadedQuery, useFragment, usePreloadedQuery } from "react-relay";

import { BookingInfo, BookingPolicy } from "@/features/BookingParts";
import { Button } from "@whoosh/design-system/src/components/controls";
import { CloseIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { PanelHeader } from "@whoosh/design-system/src/components/layout/PanelHeader";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { ClubLogo } from "../../../components/ClubLogo/ClubLogo";
import { useFlags } from "../../../utils/launchDarkly";
import { ConfirmPanelQuery } from "./__generated__/ConfirmPanelQuery.graphql";
import { ConfirmPanel_Booking$key } from "./__generated__/ConfirmPanel_Booking.graphql";
import { ActionButton } from "./components/ActionButton/ActionButton";
import { PaymentDisplay } from "./components/PaymentDisplay/PaymentDisplay";
import { PlayerList } from "./components/PlayerList/PlayerList";
import { YourRound } from "./components/YourRound/YourRound";

export const ConfirmPanel = ({
  sessionQueryRef,
  bookingRef,
  navigateHome,
  membershipTypeId,
}: {
  sessionQueryRef: PreloadedQuery<ConfirmPanelQuery>;
  bookingRef: ConfirmPanel_Booking$key | null;
  navigateHome: () => void;
  membershipTypeId: string;
}) => {
  const { newTopbar } = useFlags();
  const query = usePreloadedQuery(ConfirmPanelQueryString, sessionQueryRef);
  const session = query.session;
  const booking = useFragment(ConfirmPanelBookingFragmentString, bookingRef);
  const club = session?.currentClientProfile?.member?.club;
  const clubName = club?.name ?? t`Join Booking`;

  const isMobile = useResponsiveProp({ base: 1, md: 0 });
  const insets = useSafeAreaInsets();
  return (
    <VStack
      style={{ paddingTop: insets.top, paddingBottom: insets.bottom }}
      marginTop={isMobile ? 0 : 4}
      flex={1}
      backgroundColor={isMobile ? "surface-secondary" : "surface-primary"}
      borderTopLeftRadius={"6"}
      borderTopRightRadius={"6"}
      overflow="hidden"
      maxWidth={568}
      marginHorizontal={"auto"}
      width="100%"
    >
      <ScrollView style={{ flex: 1 }}>
        <VStack gap={isMobile ? 2 : 0}>
          {!newTopbar ? (
            <PanelHeader
              hideBottomBorder
              header={t`Join Booking`}
              subHeader={clubName}
              paddingVariant="medium"
              outerLeftComponent={
                <Button
                  accessibilityLabel={t`Close button`}
                  testID="modal-close-btn"
                  variant="secondary"
                  onPress={navigateHome}
                  leftIcon={<CloseIcon />}
                />
              }
              outerRightComponent={<ClubLogo clubRef={club ?? null} />}
            />
          ) : null}
          <BookingInfo bookingRef={booking ?? null} />
          <YourRound
            bookingRef={booking ?? null}
            sessionRef={session ?? null}
            membershipTypeId={membershipTypeId}
          />
          <PlayerList bookingRef={booking ?? null} />
          <PaymentDisplay bookingRef={booking ?? null} />
          <BookingPolicy bookingRef={booking ?? null} />
        </VStack>
      </ScrollView>
      <ActionButton bookingRef={booking ?? null} />
    </VStack>
  );
};

export const ConfirmPanelQueryString = graphql`
  query ConfirmPanelQuery {
    session {
      ...YourRound_Session
      currentClientProfile {
        __typename
        member {
          club {
            name
            ...ClubLogo_Club
          }
        }
      }
    }
  }
`;

const ConfirmPanelBookingFragmentString = graphql`
  fragment ConfirmPanel_Booking on Booking {
    ...BookingInfo_Booking
    ...BookingPolicy_Booking
    ...PaymentDisplay_Booking
    ...PlayerList_Booking
    ...ActionButton_Booking
    ...YourRound_Booking
  }
`;
